import { routes } from "configs";
import { getQuery } from "@stemscopes/api";
import { Auth } from "types";
import DashboardLink from "../components/dashboard-link";
import * as Types from "./types";
import { getBundlesFlags } from "./utils";

const getBundlesRoute: Types.GetBundlesRoute = (authData, t) => {
  let text = t("header.units");
  let title = t("header.openUnits");
  const topics = authData.school.bundleTopicIds;
  const unitTopics = authData.school.unitTopicIds;
  const [isUnit, isBundle, isSegment] = getBundlesFlags(authData);

  const queryParams = getQuery({ view: "bundles", filter: { topics } });
  const queryParamsUnits = getQuery({
    view: "bundles",
    filter: { topics: unitTopics },
  });

  const path = (): string => {
    if (isUnit && !isSegment && !isBundle) {
      return `${routes.scopes}?${queryParamsUnits}`;
    }
    if (topics?.length) {
      return `${routes.scopes}?${queryParams}`;
    }
    return "";
  };

  if (isBundle) {
    text = t("header.bundles");
    title = t("header.openBundles");
  }

  if (isSegment) {
    text = t("header.segments");
    title = t("header.openSegments");
  }

  return {
    path: path(),
    text,
    title,
    exact: true,
  };
};

const getEBooksRoute: Types.GetEBooksRoute = (t) => ({
  path: routes.eBookList,
  exact: true,
  text: t("header.eBooks"),
  title: t("header.openEBooks"),
  getIsVisible: ({ data }: Auth) => data.school.ebookSettingsAccess,
});

const getVideosAndGamesRoute: Types.GetEBooksRoute = (t) => ({
  path: routes.videosAndGames,
  exact: true,
  text: t("header.videosAndGames"),
  title: t("header.openVideosAndGames"),
  getIsVisible: ({ data }: Auth) => data.school.interactiveLibraryAccess,
});

const dashboardAnalyticsLink = {
  title: "",
  text: "",
  path: "dashboard",
  Component: DashboardLink,
  getIsVisible: (auth: Auth) =>
    (auth.data.user.profileType === "Teacher" &&
      auth.data.user.hasDashboardAccess) ||
    auth.data.user.roles.includes("DistrictAdmin") ||
    auth.data.user.roles.includes("SchoolAdmin"),
};

export const teacherSidebarLinks: Types.GetLinks = (authData, t, online) => [
  {
    path: routes.home,
    exact: true,
    text: t("header.home"),
    title: t("header.openHome"),
  },
  ...(online
    ? [
        {
          path: routes.planner,
          exact: true,
          text: t("header.planner"),
          title: t("header.openPlanner"),
        },
        getBundlesRoute(authData, t),
        {
          path: routes.scopes,
          exact: true,
          text: t("header.scopes"),
          title: t("header.openScopes"),
          descendants: [
            routes.scopes,
            routes.scope,
            routes.scopeNav,
            routes.bookmarks,
            routes.toolbox,
            routes.section,
            routes.element,
          ],
        },
        {
          path: routes.streamingDashboard,
          exact: true,
          text: t("header.streaming"),
          title: t("header.openStreaming"),
          descendants: [routes.streamingResources, routes.streamingResource],
        },
        {
          path: routes.bitsbox,
          exact: true,
          text: t("header.coding"),
          title: t("header.openCoding"),
        },
        {
          path: routes.rootStandards,
          exact: true,
          text: t("header.standards"),
          title: t("header.openStandards"),
          descendants: [routes.rootStandards, routes.standards],
        },
        {
          path: routes.students,
          exact: true,
          text: t("header.students"),
          title: t("header.openStudents"),
          descendants: [
            routes.students,
            routes.studentsAssignments,
            routes.teacherSectionAssignments,
            routes.teacherSectionsNew,
            routes.teacherSection,
            routes.teacherSectionsEdit,
            routes.teacherSectionShowStudents,
            routes.teacherSectionStudent,
            routes.teacherSectionStudentNew,
            routes.teacherSectionStudentEdit,
            routes.teacherSections,
            routes.teacherSectionsArchivedAssignments,
            routes.teacherSectionsAssignments,
            routes.teacherSectionEditAssignment,
            routes.teacherSectionSnapshotAssignment,
            routes.studentAssignment,
            routes.teacherSectionAssignmentsTeacherAssessment,
            routes.districtAssignment,
            routes.districtStudentAssignment,
          ],
        },
        {
          path: routes.teacherAssessments,
          exact: true,
          text: t("header.assessments"),
          title: t("header.openAssessments"),
          descendants: [
            routes.teacherAssessments,
            routes.teacherAssessmentsArchived,
            routes.teacherAssessmentsPending,
            routes.teacherAssessmentsNew,
            routes.teacherAssessmentsEdit,
            routes.teacherAssessment,
            routes.teacherAssessmentShare,
          ],
        },
        getEBooksRoute(t),
        {
          path: routes.help,
          exact: true,
          text: t("header.help"),
          title: t("header.openHelp"),
        },
        dashboardAnalyticsLink,
      ]
    : []),
];

export const studentSidebarLinks: Types.GetLinks = (
  authData,
  t,
  online,
  isStudentWithAssignmentHomePage = false,
) => [
  ...(isStudentWithAssignmentHomePage
    ? []
    : [
        {
          path: routes.home,
          exact: true,
          text: t("header.home"),
          title: t("header.openHome"),
        },
        getEBooksRoute(t),
      ]),
  {
    path: routes.assignments,
    exact: true,
    text: isStudentWithAssignmentHomePage
      ? t("header.home")
      : t("header.assignments"),
    title: isStudentWithAssignmentHomePage
      ? t("header.openHome")
      : t("header.openAssignments"),
    descendants: [routes.studentAssignment],
  },
  ...(online
    ? [
        {
          path: routes.grades,
          exact: true,
          text: t("header.grades"),
          title: t("header.openGrades"),
        },
        {
          path: routes.learningResources,
          exact: true,
          text: t("header.learningResources"),
          title: t("header.openLearningResources"),
        },
        {
          path: routes.studentNotes,
          exact: true,
          text: t("header.studentNotes"),
          title: t("header.openStudentNotes"),
        },
        {
          path: routes.glossaryVideos,
          exact: true,
          text: t("header.glossary"),
          title: t("header.openGlossary"),
        },
        getVideosAndGamesRoute(t),
        {
          path: routes.bitsbox,
          exact: true,
          text: t("header.coding"),
          title: t("header.openCoding"),
        },
        getEBooksRoute(t),
      ]
    : []),
];

export const kindergartenSidebarLinks: Types.GetLinks = (authData, t) => [
  {
    path: routes.home,
    exact: true,
    text: t("header.home"),
    title: t("header.openHome"),
  },
];

export const adminSidebarLinks: Types.GetLinks = (authData, t) => [
  {
    path: routes.scopes,
    exact: false,
    text: t("header.scopes"),
    title: t("header.openScopes"),
    descendants: [
      routes.scopes,
      routes.scope,
      routes.scopeNav,
      routes.bookmarks,
      routes.toolbox,
      routes.section,
      routes.element,
    ],
  },
  {
    path: routes.rootStandards,
    exact: true,
    text: t("header.standards"),
    title: t("header.openStandards"),
    descendants: [routes.rootStandards, routes.standards],
  },
  dashboardAnalyticsLink,
];

export const googleStudentSidebarLinks: Types.GetLinks = (authData, t) => [
  {
    path: `${routes.home}?google_class_room=true`,
    exact: true,
    text: t("header.home"),
    title: t("header.openHome"),
  },
];
